import React, { useState } from "react"
import { Button } from "primereact/button"
import { getAuth, multiFactor } from "firebase/auth"

import { LoaderProgressBar, LoaderSmall } from "../../../../components/Loaders"

import styles from "../../../../styles/TwoFA.module.css"

import axios from "axios"
import cloudFunctionsUrl from "../../../../config/cloudFunctionsEndpoint"
import { clearSessStorage } from "../../../../helpers/sessionStorage"

const Disenrollment = ({ factorId }) => {
  const [loading, setLoading] = useState(false)
  const [finished, setFinished] = useState(false)
  const [error, setError] = useState("")
  const [requiresReAuth, setRequiresReAuth] = useState(false)

  const auth = getAuth()
  const user = auth.currentUser

  const performLogoutCleanup = () => {
    // Clear stale RecaptchaVerifier to prevent login issues
    if (window.recaptchaVerifier) {
      try {
        window.recaptchaVerifier.clear()
      } catch (error) {
        console.warn("Error clearing reCAPTCHA:", error)
      }
      window.recaptchaVerifier = null
    }
    // Clear session storage for clean logout
    clearSessStorage("current_organization_id")
  }

  const handleClientSideRemoval = async (user, factorId) => {
    const multiFactorUser = multiFactor(user)
    const enrolledFactors = multiFactorUser.enrolledFactors

    // CRITICAL: Match on factor.factorId (not factor.uid)
    // factorId prop is "phone" or "totp" (type), not the unique uid
    const factorToRemove = enrolledFactors.find(
      (factor) => factor.factorId === factorId
    )

    if (!factorToRemove) {
      throw new Error(`MFA factor with type ${factorId} not found`)
    }

    try {
      await multiFactorUser.unenroll(factorToRemove)
    } catch (error) {
      if (error.code === "auth/requires-recent-login") {
        throw new Error("REQUIRES_RECENT_LOGIN")
      }
      throw error
    }
  }

  const handleDisable = async () => {
    setLoading(true)
    try {
      // Step 1: Try backend removal first. Axios will throw for non-2xx responses.
      await axios.post(
        `${cloudFunctionsUrl()}/clearMfaEnrolledFactor`,
        {
          uid: user.uid,
          factor_id: factorId,
        }
      )

      // Success via backend
      setFinished(true)
      setTimeout(async () => {
        performLogoutCleanup()
        await auth.signOut()
      }, 2500)
    } catch (error) {
      // Check for 409 status which requires client-side fallback
      if (
        error.response?.status === 409 ||
        error.response?.data?.details?.includes(
          "TOTP_FACTOR_REQUIRES_CLIENT_REMOVAL"
        )
      ) {
        // Fall back to client-side removal
        try {
          await handleClientSideRemoval(user, factorId)
          setFinished(true)
          setTimeout(async () => {
            performLogoutCleanup()
            await auth.signOut()
          }, 2500)
        } catch (clientError) {
          setLoading(false)
          if (clientError.message === "REQUIRES_RECENT_LOGIN") {
            setRequiresReAuth(true)
          } else {
            setError(clientError.message || "Failed to remove MFA factor")
          }
        }
        return // Stop execution after handling client-side path
      }

      // Handle other errors from backend call
      setLoading(false)
      setError(
        error.response?.data?.error ||
          error.message ||
          "Failed to remove MFA factor"
      )
    }
  }

  if (error !== "") {
    return (
      <div className={styles.setupBlock}>
        <h3>Error disabling two-step verification</h3>
        <p>{error}</p>
      </div>
    )
  }

  if (requiresReAuth) {
    return (
      <div className={styles.setupBlock}>
        <h3>
          Failed to remove MFA factor. Please log in again.{" "}
          <button
            style={{ textDecoration: "underline" }}
            onClick={() => {
              performLogoutCleanup()
              auth.signOut()
            }}
          >
            Click here to re-login
          </button>
          .
        </h3>
      </div>
    )
  }

  if (finished) {
    return (
      <div className={styles.setupBlock}>
        <h3>The disenrollment process is complete.</h3>
        <p>You will be redirected to the login page...</p>
        <LoaderSmall />
      </div>
    )
  }

  if (loading) {
    return (
      <div className={styles.setupBlock}>
        <LoaderProgressBar />
      </div>
    )
  }

  return (
    <div className={styles.setupBlock}>
      <h3>Disable two-step verification</h3>
      <p>
        If you disable factor verification, your account will not have the
        protection of a secondary sign-in step.
      </p>
      <p>
        Once you unenroll, you will need to <strong>sign in</strong> again.
      </p>
      <Button
        label="Disable"
        style={{ marginTop: 20 }}
        className="p-button-danger"
        onClick={handleDisable}
      />
    </div>
  )
}

export default Disenrollment
